import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaBriefcase,
  FaCode,
  FaReact,
  FaTrophy,
  FaMedal,
  FaCodeBranch,
  FaExternalLinkAlt,
  FaJava,
  FaShieldAlt,
  FaBookReader,
  FaChevronDown,
  FaChevronRight,
  FaBuilding,
  FaUserTie,
  FaUserCog,
  FaUserGraduate,
} from "react-icons/fa";
import {
  SiTailwindcss,
  SiMongodb,
  SiSap,
  SiMysql,
  SiLeetcode,
  SiCodeforces,
  SiNodedotjs,
  SiMui,
  SiCplusplus,
  // SiSpring,
  // SiSpringboot,
  SiC,
  SiLinux,
  SiPython,
  SiJavascript,
  SiCss3,
  SiHtml5,
  // SiPandas,
  SiCodingninjas,
  SiMedium,
  SiDevdotto,
  SiGit,
  SiGithub,
  SiNextdotjs,
  SiTypescript,
  SiRedis,
} from "react-icons/si";

const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds
const DEV_TO_CACHE_KEY = "devto_articles_cache";
const MEDIUM_CACHE_KEY = "medium_articles_cache";

async function fetchDevToArticles() {
  // 1. Try to get from cache first
  try {
    const cachedData = localStorage.getItem(DEV_TO_CACHE_KEY);
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      // Use cache if it's not expired and has data
      if (Date.now() - timestamp < CACHE_DURATION && data && data.length > 0) {
        console.log("Using cached Dev.to articles");
        return data;
      }
    }
  } catch (error) {
    console.warn("Cache retrieval failed:", error);
    // Continue to fetch fresh data
  }

  // 2. Fetch fresh data with proper error handling
  try {
    const response = await fetch(
      "https://dev.to/api/articles?username=codetirtho97",
      {
        method: "GET",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        // Add a timeout to prevent hanging requests
        signal: AbortSignal.timeout(10000), // 10 second timeout
      }
    );

    if (!response.ok) {
      throw new Error(
        `Dev.to API returned ${response.status}: ${response.statusText}`
      );
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      throw new Error("Dev.to API returned invalid data format");
    }

    console.log("Dev.to API returned", data.length, "articles");

    // UPDATED: Sort the articles by public reactions (most popular first), then by date
    data.sort((a, b) => {
      const reactionsA = a.public_reactions_count || 0;
      const reactionsB = b.public_reactions_count || 0;

      // Primary sort by reactions (descending)
      if (reactionsB !== reactionsA) {
        return reactionsB - reactionsA;
      }

      // Secondary sort by date (newest first) if reactions are equal
      return new Date(b.published_at) - new Date(a.published_at);
    });

    // Process and transform the data, limiting to 7 articles
    const processedArticles = data.slice(0, 7).map((article) => ({
      id: article.id,
      title: article.title,
      cover: article.cover_image || "/images/default_cover.jpg",
      date: new Date(article.published_at).toDateString(),
      link: article.url,
      reactions: article.public_reactions_count || 0,
      tags: article.tag_list || [],
      // Add a formatted date field for easier sorting/display
      formattedDate: new Date(article.published_at).toLocaleString(),
      published_at: article.published_at,
      // Add reading time if available
      readingTime: article.reading_time_minutes || 0,
    }));

    // Log the processed articles for debugging
    console.log(
      "Processed Dev.to articles (sorted by reactions):",
      processedArticles.map((a) => ({
        title: a.title.substring(0, 30) + "...",
        reactions: a.reactions,
        date: a.formattedDate,
      }))
    );

    // Save to cache
    localStorage.setItem(
      DEV_TO_CACHE_KEY,
      JSON.stringify({
        data: processedArticles,
        timestamp: Date.now(),
      })
    );

    return processedArticles;
  } catch (error) {
    console.error("Dev.to fetch failed:", error);

    // 3. Fall back to cached data even if expired
    try {
      const cachedData = localStorage.getItem(DEV_TO_CACHE_KEY);
      if (cachedData) {
        const { data } = JSON.parse(cachedData);
        if (data && data.length > 0) {
          console.log("Using expired Dev.to cache as fallback");
          return data;
        }
      }
    } catch (e) {
      console.error("Cache fallback failed:", e);
    }

    // 4. Last resort - return empty array (no hardcoded data)
    console.warn("No Dev.to articles available - using empty array");
    return [];
  }
}

async function fetchMediumArticles() {
  // 1. Try to get from cache first
  try {
    const cachedData = localStorage.getItem(MEDIUM_CACHE_KEY);
    if (cachedData) {
      const { data, timestamp } = JSON.parse(cachedData);
      // Use cache if it's not expired and has data
      if (Date.now() - timestamp < CACHE_DURATION && data && data.length > 0) {
        console.log("Using cached Medium articles");
        return data;
      }
    }
  } catch (error) {
    console.warn("Medium cache retrieval failed:", error);
  }

  // 2. Fetch fresh data with proper error handling
  try {
    const response = await fetch(
      "https://api.rss2json.com/v1/api.json?rss_url=https://medium.com/feed/@codetirtho97",
      {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal: AbortSignal.timeout(10000), // 10 second timeout
      }
    );

    if (!response.ok) {
      throw new Error(
        `Medium API returned ${response.status}: ${response.statusText}`
      );
    }

    const mediumData = await response.json();

    if (mediumData.status !== "ok" || !Array.isArray(mediumData.items)) {
      throw new Error("Medium API returned invalid data format");
    }

    // Process and transform the data
    const processedArticles = mediumData.items.slice(0, 30).map((article) => ({
      id: article.guid,
      title: article.title,
      // Extract published date for better formatting options
      publishedDate: new Date(article.pubDate),
      date: new Date(article.pubDate).toDateString(),
      link: article.link,
      // Add any thumbnail if available (some RSS feeds include them)
      thumbnail: article.thumbnail || null,
      // Extract author info if available
      author: article.author || "Tirthoraj Bhattacharya",
      // Extract categories/tags if available
      categories: Array.isArray(article.categories) ? article.categories : [],
    }));

    // Save to cache
    localStorage.setItem(
      MEDIUM_CACHE_KEY,
      JSON.stringify({
        data: processedArticles,
        timestamp: Date.now(),
      })
    );

    return processedArticles;
  } catch (error) {
    console.error("Medium fetch failed:", error);

    // 3. Fall back to cached data even if expired
    try {
      const cachedData = localStorage.getItem(MEDIUM_CACHE_KEY);
      if (cachedData) {
        const { data } = JSON.parse(cachedData);
        if (data && data.length > 0) {
          console.log("Using expired Medium cache as fallback");
          return data;
        }
      }
    } catch (e) {
      console.error("Medium cache fallback failed:", e);
    }

    // 4. Last resort - return empty array
    console.warn("No Medium articles available - using empty array");
    return [];
  }
}

function Experience() {
  const [activeTab, setActiveTab] = useState("skills");
  const [activeSkillsTab, setActiveSkillsTab] = useState("Languages");
  const [expandedJob, setExpandedJob] = useState(null);
  const [expandedCodingIndex, setExpandedCodingIndex] = useState([]);
  const [activePubTab, setActivePubTab] = useState("devto");
  const [devToArticles, setDevToArticles] = useState([]);
  const [mediumArticles, setMediumArticles] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    async function fetchArticles() {
      try {
        // Fetch both Dev.to and Medium articles in parallel
        const [devToArticles, mediumArticles] = await Promise.all([
          fetchDevToArticles(),
          fetchMediumArticles(),
        ]);

        setDevToArticles(devToArticles);
        setMediumArticles(mediumArticles);
      } catch (error) {
        console.error("Error fetching articles:", error);
      }
    }

    fetchArticles();
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("animate-fade-in");
        }
      },
      { threshold: 0.1 }
    );

    const currentSection = sectionRef.current;
    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  // function getPlatformUrl(platformName) {
  //   const username = "CodeTirtho97"; // Your username

  //   switch (platformName.toLowerCase()) {
  //     case "leetcode":
  //       return `https://leetcode.com/u/${username}`;
  //     case "code360": // Not sure about the exact URL format, adjust if needed
  //       return "https://www.naukri.com/code360/profile/CodeTirtho";
  //     case "codeforces":
  //       return `https://codeforces.com/profile/${username}`;
  //     default:
  //       return `https://www.google.com/search?q=${platformName}+${username}`; // Fallback
  //   }
  // }

  // Experience data
  const experience = {
    company: "TATA CONSULTANCY SERVICES",
    positions: [
      {
        title: "IT Analyst",
        duration: "2023 - 2024",
        description: [
          "Led SAP infrastructure upgrades across 3 client environments, streamlining deployment processes and reducing downtime by 25%.",
          "Built predictive maintenance tools that cut onboarding time by 30% and reduced critical system failures.",
          "Implemented automated monitoring solutions for enterprise systems, ensuring 99% uptime across production environments.",
        ],
      },
      {
        title: "System Engineer",
        duration: "2021 - 2023",
        description: [
          "Created SAP integration solutions with custom APIs, improving system upgrade processing time by 25%.",
          "Optimized database performance through custom tooling, improving SQL query execution by 20% and automating 50% of tasks.",
          "Orchestrated cross-functional SAP modifications, delivering seamless system updates with zero business disruption.",
        ],
      },
      {
        title: "Assistant System Engineer",
        duration: "2019 - 2021",
        description: [
          "Developed automated scripts for ticket processing and system monitoring, improving resolution efficiency by 20%.",
          "Enhanced SAP user authorization workflows through process optimization, reducing manual setup time by 30%.",
          "Managed 100+ daily client requests with 95% resolution rate, maintaining high-quality service standards.",
        ],
      },
    ],
  };

  const skills = {
    Languages: [
      {
        icon: SiCplusplus,
        name: "C++",
        level: "Intermediate",
        color: "#00a8e8",
      },
      { icon: SiC, name: "C", level: "Intermediate", color: "#b5179e" },
      { icon: FaJava, name: "Java", level: "Intermediate", color: "#0077b6" },
      {
        icon: SiPython,
        name: "Python",
        level: "Intermediate",
        color: "#b79ced",
      },
      { icon: SiHtml5, name: "HTML5", level: "Proficient", color: "#E34F26" },
      { icon: SiCss3, name: "CSS3", level: "Intermediate", color: "#1572B6" },
      {
        icon: SiJavascript,
        name: "JavaScript",
        level: "Intermediate",
        color: "#f9dc5c",
      },
      {
        icon: SiTypescript,
        name: "TypeScript",
        level: "Basic",
        color: "#79b8ff",
      },
    ],
    Frameworks: [
      {
        icon: FaReact,
        name: "React.js",
        level: "Intermediate",
        color: "#77b7d7",
      },
      { icon: SiNextdotjs, name: "Next.js", level: "Basic", color: "#d17724" },
      // { icon: SiSpring, name: "Spring", level: "Basic", color: "#80ea6e" },
      // {
      //   icon: SiSpringboot,
      //   name: "SpringBoot",
      //   level: "Basic",
      //   color: "#6db33f",
      // },
      {
        icon: SiNodedotjs,
        name: "Node.js",
        level: "Intermediate",
        color: "#84ba64",
      },
      {
        icon: SiTailwindcss,
        name: "Tailwind",
        level: "Basic",
        color: "#06B6D4",
      },
      { icon: SiMui, name: "MaterialUI", level: "Basic", color: "#006bd6" },
    ],
    Databases: [
      { icon: SiMongodb, name: "MongoDB", level: "Basic", color: "#40916c" },
      { icon: SiMysql, name: "MySQL", level: "Intermediate", color: "#48cae4" },
      { icon: SiRedis, name: "Redis", level: "Basic", color: "#FF4438" },
    ],
    Tools: [
      { icon: SiSap, name: "SAP", level: "Intermediate", color: "#0070f2" },
      { icon: SiGit, name: "Git", level: "Intermediate", color: "#f14e32" },
      {
        icon: SiGithub,
        name: "GitHub",
        level: "Intermediate",
        color: "#adb5bd",
      },
      {
        icon: FaCode,
        name: "VSCode",
        level: "Intermediate",
        color: "#0078d4",
      },
      // { icon: SiPandas, name: "Pandas", level: "Basic", color: "#9d4edd" },
      { icon: SiLinux, name: "Linux/Unix", level: "Basic", color: "#ff6c0e" },
    ],
  };

  const codingAchievements = [
    {
      platform: "LeetCode",
      icon: SiLeetcode,
      stats: [
        { label: "Problems Solved", value: "250+", icon: FaCodeBranch },
        { label: "Contest Rating", value: "1445", icon: FaTrophy },
        { label: "Global Rank", value: "Top 50%", icon: FaMedal },
      ],
      color: "#FFA116",
    },
    {
      platform: "Code360",
      icon: SiCodingninjas,
      stats: [
        { label: "Problems Solved", value: "180+", icon: FaCodeBranch },
        { label: "Badges Earned", value: "12", icon: FaMedal },
        { label: "Global Rank", value: "GrandMaster", icon: FaTrophy },
      ],
      color: "#f66c3b",
    },
    {
      platform: "CodeForces",
      icon: SiCodeforces,
      stats: [
        { label: "Max Rating", value: "1422", icon: FaTrophy },
        { label: "Contests", value: "15+", icon: FaShieldAlt },
        { label: "Problems Solved", value: "150+", icon: FaCodeBranch },
      ],
      color: "#318CE7",
    },
  ];

  // Define the main tabs
  const tabs = [
    { id: "experience", label: "Professional Experience", icon: FaBriefcase },
    { id: "skills", label: "Technical Skills", icon: FaCode },
    { id: "coding", label: "Coding Profiles", icon: FaTrophy },
    { id: "publications", label: "Publications", icon: FaBookReader },
  ];

  // Define skills tabs
  const skillsTabs = ["Languages", "Frameworks", "Databases", "Tools"];

  return (
    <div className="min-h-screen bg-gray-900 py-16 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 opacity-95"></div>

      <div className="max-w-7xl mx-auto relative z-10" ref={sectionRef}>
        {/* Page Title */}
        <motion.h2
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-4xl md:text-6xl font-bold text-center font-['Inter'] mb-12 mt-16 text-gray-100"
        >
          <span className="text-emerald-400 hover:cursor-default">
            Professional Journey & Skills
          </span>
          <p className="text-white text-lg font-bold mt-3 font-['Inter'] hover:cursor-default">
            Crafting solutions, mastering skills.
          </p>
        </motion.h2>

        {/* Removed "At-a-Glance" summary section */}

        {/* Enhanced Tabbed Navigation with glowing effects */}
        <div className="flex overflow-x-auto mb-8 pb-2 no-scrollbar">
          <div className="flex space-x-3 mx-auto relative">
            {tabs.map((tab) => (
              <motion.button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`flex items-center px-5 py-3 rounded-lg transition-all duration-300 whitespace-nowrap relative ${
                  activeTab === tab.id
                    ? "bg-emerald-400/20 text-emerald-400 border border-emerald-400 shadow-lg shadow-emerald-400/20"
                    : "bg-gray-800/40 text-gray-400 hover:bg-gray-700/40 hover:text-emerald-400 border border-gray-700"
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="tab-background"
                    className="absolute inset-0 bg-emerald-400/5 rounded-lg -z-10"
                    transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  />
                )}
                <tab.icon className="mr-2" />
                <span className="font-medium">{tab.label}</span>
              </motion.button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        <div className="mt-6">
          {/* Professional Experience Tab */}
          {activeTab === "experience" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25 overflow-hidden"
            >
              <div className="flex items-center mb-8 cursor-default relative">
                <div className="absolute -top-6 -left-6 w-24 h-24 bg-emerald-400/10 rounded-full blur-2xl"></div>
                <div className="p-3 bg-emerald-400/10 rounded-lg mr-4 relative">
                  <FaBuilding className="text-emerald-400 text-2xl" />
                </div>
                <h3 className="text-xl md:text-2xl font-semibold text-emerald-400 tracking-wide">
                  {experience.company}
                </h3>
              </div>

              {/* Enhanced Interactive Timeline */}
              <div className="relative pl-10 pb-2">
                <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-emerald-300 via-emerald-400 to-emerald-600 rounded-full shadow-lg shadow-emerald-400/20"></div>

                {experience.positions.map((position, index) => (
                  <div key={index} className="mb-12 last:mb-0">
                    {/* Timeline node with visual enhancement */}
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      className="absolute -left-2 w-5 h-5 bg-gradient-to-br from-emerald-300 to-emerald-600 rounded-full border-2 border-gray-900 cursor-pointer shadow-lg shadow-emerald-400/40 z-10"
                      onClick={() =>
                        setExpandedJob(expandedJob === index ? null : index)
                      }
                    />

                    {/* Position Year Label */}
                    <div className="absolute -left-32 top-0 w-24 text-right">
                      <div className="text-emerald-400 font-bold opacity-80">
                        {position.duration.split(" - ")[0]}
                      </div>
                    </div>

                    {/* Position Card */}
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{
                        duration: 0.4,
                        delay: index * 0.1,
                        ease: "easeOut",
                      }}
                      className={`bg-gray-800/50 backdrop-blur-sm rounded-xl shadow-xl border ${
                        expandedJob === index
                          ? "border-emerald-400 bg-gradient-to-br from-gray-800/80 to-gray-900/80"
                          : "border-gray-700 hover:border-emerald-400/50 hover:shadow-emerald-400/5"
                      } transition-all duration-300 cursor-pointer overflow-hidden group`}
                      onClick={() =>
                        setExpandedJob(expandedJob === index ? null : index)
                      }
                    >
                      {/* Subtle gradient overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                      {/* Job Header with improved styling */}
                      <div className="p-5 flex flex-col sm:flex-row sm:justify-between sm:items-center relative">
                        <div className="flex items-center mb-3 sm:mb-0">
                          <div className="p-2 bg-emerald-400/10 rounded-lg mr-4">
                            {index === 0 ? (
                              <FaUserTie className="text-emerald-400 text-xl flex-shrink-0" />
                            ) : index === 1 ? (
                              <FaUserCog className="text-emerald-400 text-xl flex-shrink-0" />
                            ) : (
                              <FaUserGraduate className="text-emerald-400 text-xl flex-shrink-0" />
                            )}
                          </div>
                          <div>
                            <h4 className="text-xl font-semibold text-gray-100 group-hover:text-emerald-400 transition-colors">
                              {position.title}
                            </h4>
                            <p className="text-gray-400 text-sm">
                              {position.duration}
                            </p>
                          </div>
                        </div>
                        <motion.div
                          animate={{ rotate: expandedJob === index ? 180 : 0 }}
                          transition={{
                            duration: 0.4,
                            ease: [0.4, 0.0, 0.2, 1],
                          }}
                          className="w-8 h-8 rounded-full bg-emerald-400/10 flex items-center justify-center text-emerald-400"
                        >
                          <FaChevronDown />
                        </motion.div>
                      </div>

                      {/* Job Details with smoother animation */}
                      <AnimatePresence>
                        {expandedJob === index && (
                          <motion.div
                            key={`job-details-${index}`}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{
                              height: "auto",
                              opacity: 1,
                              transition: {
                                height: {
                                  duration: 0.4,
                                  ease: [0.4, 0.0, 0.2, 1],
                                },
                                opacity: { duration: 0.3, delay: 0.1 },
                              },
                            }}
                            exit={{
                              height: 0,
                              opacity: 0,
                              transition: {
                                height: {
                                  duration: 0.3,
                                  ease: [0.4, 0.0, 0.2, 1],
                                },
                                opacity: { duration: 0.2 },
                              },
                            }}
                            className="overflow-hidden"
                          >
                            <div className="px-5 pb-5">
                              <div className="pl-4 border-l-2 border-emerald-400/30 space-y-4 pt-2">
                                {position.description.map((item, itemIndex) => (
                                  <motion.div
                                    key={itemIndex}
                                    initial={{ opacity: 0, x: -10 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{
                                      duration: 0.3,
                                      delay: 0.1 + itemIndex * 0.1,
                                    }}
                                    className="flex group/item"
                                  >
                                    <div className="mt-1 mr-3 flex-shrink-0 w-5 h-5 rounded-full bg-emerald-400/10 flex items-center justify-center">
                                      <FaChevronRight className="text-emerald-400 text-xs group-hover/item:text-emerald-300" />
                                    </div>
                                    <p className="text-gray-300 text-sm leading-relaxed group-hover/item:text-white transition-colors">
                                      {item}
                                    </p>
                                  </motion.div>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Technical Skills Tab */}
          {activeTab === "skills" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25"
            >
              {/* Skills Categories - Redesigned to be more distinctive and less like nested tabs */}
              <div className="mb-10 relative">
                <div className="flex items-center justify-center">
                  <div className="inline-flex bg-gray-800/50 p-1.5 rounded-full shadow-lg border border-gray-700">
                    {skillsTabs.map((tab) => (
                      <motion.button
                        key={tab}
                        onClick={() => setActiveSkillsTab(tab)}
                        className={`px-6 py-2.5 rounded-full text-sm font-medium transition-all duration-300 relative overflow-hidden ${
                          activeSkillsTab === tab
                            ? "text-gray-900"
                            : "text-gray-400 hover:text-white"
                        }`}
                      >
                        {activeSkillsTab === tab && (
                          <motion.div
                            layoutId="skills-pill-bg"
                            className="absolute inset-0 bg-emerald-400"
                            style={{ borderRadius: 9999 }}
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 30,
                            }}
                          />
                        )}
                        <span className="relative z-10">{tab}</span>
                      </motion.button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Skills Grid with revised styling to match pill selector */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                {skills[activeSkillsTab].map((skill, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                    className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700/50 hover:border-emerald-400/30 transition-all duration-300 flex flex-col items-center group shadow-lg hover:shadow-emerald-400/5"
                  >
                    <div className="p-3 bg-gray-900/50 rounded-full mb-4 group-hover:bg-emerald-400/10 transition-colors duration-300">
                      <skill.icon
                        className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
                        style={{ color: skill.color }}
                      />
                    </div>
                    <p className="text-gray-200 font-medium text-center mb-3">
                      {skill.name}
                    </p>

                    {/* Skill Level Indicator - Fixed for proper dot display */}
                    <div className="mt-auto flex items-center">
                      <div className="flex space-x-1">
                        <div
                          className={`w-3 h-3 rounded-full ${
                            skill.level === "Basic" ||
                            skill.level === "Intermediate" ||
                            skill.level === "Proficient"
                              ? "bg-emerald-400"
                              : "bg-gray-600"
                          }`}
                        ></div>
                        <div
                          className={`w-3 h-3 rounded-full ${
                            skill.level === "Intermediate" ||
                            skill.level === "Proficient"
                              ? "bg-emerald-400"
                              : "bg-gray-600"
                          }`}
                        ></div>
                        <div
                          className={`w-3 h-3 rounded-full ${
                            skill.level === "Proficient"
                              ? "bg-emerald-400"
                              : "bg-gray-600"
                          }`}
                        ></div>
                      </div>
                      <span className="ml-2 text-xs text-gray-400">
                        {skill.level}
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Coding Profiles Tab */}
          {activeTab === "coding" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25"
            >
              <h3 className="text-3xl font-bold text-gray-100 mb-8 flex items-center cursor-default">
                <FaTrophy className="text-emerald-400 mr-3" />
                Coding Journey
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {codingAchievements.map((platform, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.2 }}
                    className={`relative bg-gray-800/50 border ${
                      expandedCodingIndex && expandedCodingIndex.includes(index)
                        ? "border-emerald-400"
                        : "border-gray-700"
                    } rounded-xl shadow-lg overflow-hidden cursor-pointer group transition-all duration-300`}
                    onClick={() => {
                      // Modified to allow multiple expanded sections
                      setExpandedCodingIndex((prev) => {
                        if (!prev) prev = [];
                        if (prev.includes(index)) {
                          return prev.filter((i) => i !== index);
                        } else {
                          return [...prev, index];
                        }
                      });
                    }}
                    whileHover={{ scale: 1.03 }}
                  >
                    {/* Background glow effect */}
                    <div
                      className="absolute inset-0 bg-gradient-to-br"
                      style={{
                        backgroundImage: `linear-gradient(to bottom right, ${platform.color}20, transparent, transparent)`,
                        opacity:
                          expandedCodingIndex &&
                          expandedCodingIndex.includes(index)
                            ? "0.3"
                            : "0",
                      }}
                    ></div>

                    {/* Header */}
                    <div className="flex justify-between items-center p-5 border-b border-gray-700/50">
                      <div className="flex items-center space-x-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ backgroundColor: `${platform.color}20` }}
                        >
                          <platform.icon
                            size={26}
                            style={{ color: platform.color }}
                          />
                        </div>
                        <h4 className="text-xl font-semibold text-gray-100">
                          {platform.platform}
                        </h4>
                      </div>
                      <motion.div
                        animate={{
                          rotate:
                            expandedCodingIndex &&
                            expandedCodingIndex.includes(index)
                              ? 180
                              : 0,
                        }}
                        transition={{ duration: 0.3 }}
                        className="text-emerald-400"
                      >
                        <FaChevronDown />
                      </motion.div>
                    </div>

                    {/* Preview Stats (visible when collapsed) */}
                    {(!expandedCodingIndex ||
                      !expandedCodingIndex.includes(index)) && (
                      <div className="p-5 grid grid-cols-2 gap-3">
                        {platform.stats.slice(0, 2).map((stat, statIndex) => (
                          <div key={statIndex} className="flex items-center">
                            <div
                              className="p-1.5 rounded-md mr-2"
                              style={{ backgroundColor: `${platform.color}20` }}
                            >
                              <stat.icon
                                size={14}
                                style={{ color: platform.color }}
                              />
                            </div>
                            <div>
                              <p className="text-white font-medium">
                                {stat.value}
                              </p>
                              <p className="text-xs text-gray-400">
                                {stat.label}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Expanded Stats */}
                    <AnimatePresence>
                      {expandedCodingIndex &&
                        expandedCodingIndex.includes(index) && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="p-5"
                          >
                            <div className="grid grid-cols-3 gap-3 mb-4">
                              {platform.stats.map((stat, statIndex) => (
                                <motion.div
                                  key={statIndex}
                                  className="bg-gray-900/40 p-3 rounded-lg border border-gray-700 hover:border-emerald-400/50 transition-all duration-300"
                                  whileHover={{ scale: 1.05 }}
                                >
                                  <div className="flex items-center mb-2">
                                    <div
                                      className="p-1.5 rounded-md mr-2"
                                      style={{
                                        backgroundColor: `${platform.color}20`,
                                      }}
                                    >
                                      <stat.icon
                                        size={16}
                                        style={{ color: platform.color }}
                                      />
                                    </div>
                                    <p className="text-gray-400 text-xs">
                                      {stat.label}
                                    </p>
                                  </div>
                                  {/* Fixed truncation issue by controlling size - add max-w-full and truncate */}
                                  <p className="text-white text-xl font-bold max-w-full truncate">
                                    {stat.value}
                                  </p>
                                </motion.div>
                              ))}
                            </div>

                            {/* Additional data based on platform */}
                            {index === 0 && ( // LeetCode (first platform)
                              <div className="mt-4 bg-gray-900/30 p-3 rounded-lg">
                                <h5 className="text-sm text-gray-300 mb-3">
                                  Progress by Category
                                </h5>
                                <div className="space-y-3">
                                  {[
                                    {
                                      category: "Arrays",
                                      completed: 85,
                                      total: 120,
                                    },
                                    {
                                      category: "Dynamic Programming",
                                      completed: 45,
                                      total: 80,
                                    },
                                    {
                                      category: "Trees",
                                      completed: 32,
                                      total: 60,
                                    },
                                  ].map((category, idx) => (
                                    <div key={idx}>
                                      <div className="flex justify-between text-xs mb-1">
                                        <span className="text-gray-300">
                                          {category.category}
                                        </span>
                                        <span className="text-emerald-400">
                                          {category.completed}/{category.total}
                                        </span>
                                      </div>
                                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full"
                                          style={{
                                            width: `${
                                              (category.completed /
                                                category.total) *
                                              100
                                            }%`,
                                            backgroundColor: platform.color,
                                          }}
                                        ></div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {index === 0 && ( // LeetCode (first platform)
                              <div className="mt-4 bg-gray-900/30 p-3 rounded-lg">
                                <h5 className="text-sm text-gray-300 mb-3">
                                  Recent Activity
                                </h5>
                                <div className="space-y-2">
                                  <div className="flex justify-between items-center text-xs p-2 bg-gray-800/40 rounded-lg">
                                    <span className="text-gray-400">
                                      May 15, 2025
                                    </span>
                                    <span className="text-white">
                                      Weekly Contest 391
                                    </span>
                                    <span className="text-emerald-400">
                                      Solved 3/4
                                    </span>
                                  </div>
                                  <div className="flex justify-between items-center text-xs p-2 bg-gray-800/40 rounded-lg">
                                    <span className="text-gray-400">
                                      May 8, 2025
                                    </span>
                                    <span className="text-white">
                                      Biweekly Contest 126
                                    </span>
                                    <span className="text-emerald-400">
                                      Solved 2/4
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* Visit profile link - Enhanced with proper link and hover effects */}
                            {/* <a
                              href={getPlatformUrl(platform.platform)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-4 w-full flex items-center justify-center py-2 px-4 bg-gray-900/50 hover:bg-emerald-500/20 text-white hover:text-emerald-300 rounded-lg transition-all duration-300 border border-gray-700 hover:border-emerald-400 group/link"
                            >
                              <span>Visit Profile</span>
                              <FaExternalLinkAlt className="ml-2 text-xs transition-transform duration-300 group-hover/link:translate-x-1" />
                            </a> */}
                          </motion.div>
                        )}
                    </AnimatePresence>
                  </motion.div>
                ))}
              </div>

              {/* Codolio Card Link (CTA) */}
              <motion.a
                href="https://codolio.com/profile/CodeTirtho97/card"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.6 }}
                className="block relative overflow-hidden rounded-xl border border-emerald-400/30 shadow-lg transform transition-all duration-300 hover:scale-105 hover:shadow-2xl bg-gradient-to-r from-gray-900 via-emerald-900/30 to-gray-900 group hover:border-emerald-400"
              >
                {/* Glowing Border Animation */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-emerald-400/20 to-transparent opacity-25 group-hover:opacity-50 transition-all duration-500"></div>

                {/* Card Content */}
                <div className="p-6 flex items-center justify-between relative z-10">
                  <div className="flex items-center">
                    {/* Icon with Background Glow */}
                    <div className="bg-emerald-500/20 p-3 rounded-lg shadow-lg mr-4 group-hover:bg-emerald-500/40 transition-colors duration-300">
                      <FaCode className="w-8 h-8 text-emerald-400 group-hover:text-emerald-300" />
                    </div>

                    {/* Text Content */}
                    <div>
                      <h4 className="text-gray-100 font-bold text-xl tracking-wide group-hover:text-white transition-colors duration-300">
                        <span className="text-emerald-400 group-hover:text-emerald-300 transition-colors duration-300">
                          Explore
                        </span>{" "}
                        My Complete Journey
                      </h4>
                      <p className="text-gray-300 text-sm mt-1 group-hover:text-gray-200 transition-colors duration-300">
                        View detailed stats and achievements on{" "}
                        <span className="text-emerald-400 font-medium group-hover:text-emerald-300 transition-colors duration-300">
                          Codolio
                        </span>
                      </p>
                    </div>
                  </div>

                  {/* Visit Button with enhanced hover effect */}
                  <div className="flex items-center group-hover:translate-x-1 transition-transform duration-300">
                    <span className="mr-2 text-emerald-300 font-semibold text-md group-hover:text-emerald-200 transition-colors duration-300">
                      Visit
                    </span>
                    <FaExternalLinkAlt className="w-5 h-5 text-emerald-300 group-hover:text-emerald-200 transition-colors duration-300" />
                  </div>
                </div>
              </motion.a>
            </motion.div>
          )}

          {/* Publications Tab */}
          {activeTab === "publications" && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5 }}
              className="bg-gray-800/25 rounded-xl shadow-lg p-6 border border-emerald-400/25"
            >
              {/* Toggle between Dev.to and Medium */}
              <div className="flex justify-center mb-8">
                <div className="bg-gray-800/60 p-1 rounded-lg inline-flex">
                  <button
                    onClick={() => setActivePubTab("devto")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 flex items-center ${
                      activePubTab === "devto"
                        ? "bg-emerald-400/20 text-emerald-400"
                        : "text-gray-400 hover:text-emerald-400"
                    }`}
                  >
                    <SiDevdotto className="mr-2" />
                    Dev.to Articles
                  </button>
                  <button
                    onClick={() => setActivePubTab("medium")}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 flex items-center ${
                      activePubTab === "medium"
                        ? "bg-emerald-400/20 text-emerald-400"
                        : "text-gray-400 hover:text-emerald-400"
                    }`}
                  >
                    <SiMedium className="mr-2" />
                    Medium Articles
                  </button>
                </div>
              </div>

              {/* Dev.to Articles */}
              {activePubTab === "devto" && (
                <div>
                  <div className="flex justify-between items-center mb-8">
                    <h4 className="text-2xl font-medium text-gray-100 flex items-center">
                      <SiDevdotto className="text-orange-600 mr-2 text-3xl" />
                      Popular Articles
                      <span className="ml-2 text-sm text-gray-400 font-normal">
                        (sorted by engagement)
                      </span>
                    </h4>
                    <a
                      href="https://dev.to/codetirtho97"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-sm font-medium text-emerald-500 border border-emerald-500 hover:bg-emerald-500 hover:text-white rounded-lg shadow transition-transform transform hover:scale-105"
                    >
                      See More →
                    </a>
                  </div>

                  {devToArticles.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-400"></div>
                      <p className="mt-4 text-gray-400">Loading articles...</p>
                    </div>
                  ) : (
                    <div className="space-y-8">
                      {/* Featured Article (Most Popular) */}
                      {devToArticles.length > 0 && (
                        <motion.a
                          href={devToArticles[0].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.5 }}
                          className="group block bg-gradient-to-r from-orange-900/20 via-gray-800/40 to-gray-900/40 rounded-2xl overflow-hidden border border-orange-500/20 hover:border-orange-400/40 shadow-xl hover:shadow-orange-400/10 transition-all duration-300"
                        >
                          <div className="flex flex-col lg:flex-row">
                            {/* Image Section */}
                            <div className="lg:w-2/5 h-64 lg:h-auto relative overflow-hidden">
                              <img
                                src={devToArticles[0].cover}
                                alt={devToArticles[0].title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) => {
                                  e.target.src = "/images/default_cover.jpg";
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent group-hover:from-black/40 transition-all duration-300"></div>

                              {/* Floating Badge */}
                              <div className="absolute top-4 left-4 flex items-center gap-2">
                                <div className="bg-orange-500/90 text-white text-xs px-3 py-1.5 rounded-full font-bold flex items-center gap-1">
                                  <SiDevdotto className="text-sm" />
                                  FEATURED
                                </div>
                                {devToArticles[0].reactions > 0 && (
                                  <div className="bg-pink-500/90 text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1">
                                    <span>❤️</span>
                                    {devToArticles[0].reactions}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Content Section */}
                            <div className="lg:w-3/5 p-8 flex flex-col justify-center">
                              <div className="flex flex-wrap gap-2 mb-4">
                                {devToArticles[0].tags
                                  .slice(0, 3)
                                  .map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-3 py-1 bg-orange-400/20 text-orange-300 border border-orange-400/30 rounded-full"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                              </div>

                              <h3 className="text-2xl lg:text-3xl font-bold text-gray-100 group-hover:text-orange-400 transition-colors mb-4 leading-tight">
                                {devToArticles[0].title}
                              </h3>

                              <div className="flex items-center justify-between text-sm text-gray-400 mb-6">
                                <span>{devToArticles[0].date}</span>
                                {devToArticles[0].readingTime > 0 && (
                                  <span className="flex items-center gap-1">
                                    <FaBookReader className="text-xs" />
                                    {devToArticles[0].readingTime} min read
                                  </span>
                                )}
                              </div>

                              <div className="flex items-center text-orange-400 font-medium group-hover:text-orange-300 transition-colors">
                                <span className="mr-2">Read Full Article</span>
                                <FaExternalLinkAlt className="text-sm transition-transform duration-300 group-hover:translate-x-1" />
                              </div>
                            </div>
                          </div>
                        </motion.a>
                      )}

                      {/* Regular Articles Grid */}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {devToArticles.slice(1, 6).map((article, index) => (
                          <motion.a
                            key={article.id || index}
                            href={article.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{
                              duration: 0.5,
                              delay: (index + 1) * 0.1,
                            }}
                            className="group bg-gray-800/40 border border-gray-700/50 hover:border-orange-400/40 rounded-xl overflow-hidden shadow-lg hover:shadow-orange-400/10 transform hover:scale-[1.02] transition-all duration-300 flex flex-col h-full relative"
                          >
                            {/* Popularity Indicator */}
                            {article.reactions > 0 && (
                              <div className="absolute top-3 right-3 z-10 bg-pink-500/90 text-white text-xs px-2.5 py-1 rounded-full font-medium flex items-center gap-1 shadow-lg">
                                <span>❤️</span>
                                {article.reactions}
                              </div>
                            )}

                            {/* Image Container */}
                            <div className="h-48 overflow-hidden relative">
                              <img
                                src={article.cover}
                                alt={article.title}
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                onError={(e) => {
                                  e.target.src = "/images/default_cover.jpg";
                                }}
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

                              {/* Primary Tag */}
                              {article.tags && article.tags.length > 0 && (
                                <div className="absolute bottom-3 left-3 bg-gray-900/90 text-orange-400 text-xs px-3 py-1.5 rounded-full font-medium border border-orange-400/30">
                                  #{article.tags[0]}
                                </div>
                              )}
                            </div>

                            {/* Content Container */}
                            <div className="p-5 flex flex-col flex-grow">
                              <h4 className="text-lg font-bold text-gray-100 group-hover:text-orange-400 transition-colors line-clamp-2 mb-3 leading-tight">
                                {article.title}
                              </h4>

                              {/* Article Stats */}
                              <div className="flex items-center justify-between text-xs text-gray-400 mb-4">
                                <span>{article.date}</span>
                                {article.readingTime > 0 && (
                                  <span className="flex items-center gap-1">
                                    <FaBookReader />
                                    {article.readingTime}m
                                  </span>
                                )}
                              </div>

                              {/* Bottom Section */}
                              <div className="mt-auto flex items-center justify-between">
                                <div className="flex flex-wrap gap-1">
                                  {article.tags.slice(1, 3).map((tag, idx) => (
                                    <span
                                      key={idx}
                                      className="text-xs px-2 py-0.5 bg-gray-700/50 text-gray-400 rounded"
                                    >
                                      #{tag}
                                    </span>
                                  ))}
                                </div>

                                <span className="text-orange-400 text-sm flex items-center opacity-0 group-hover:opacity-100 transition-all duration-300 font-medium">
                                  Read
                                  <FaExternalLinkAlt className="ml-1.5 text-xs group-hover:translate-x-1 transition-transform duration-300" />
                                </span>
                              </div>
                            </div>
                          </motion.a>
                        ))}
                      </div>

                      {/* Call to Action */}
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1 }}
                        className="text-center mt-8"
                      >
                        <a
                          href="https://dev.to/codetirtho97"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-600/20 to-orange-500/20 hover:from-orange-600 hover:to-orange-500 text-orange-400 hover:text-white px-6 py-3 rounded-xl border border-orange-500/30 hover:border-orange-500 transition-all duration-300 font-medium shadow-lg hover:shadow-orange-400/20"
                        >
                          <SiDevdotto className="text-lg" />
                          Explore All Articles on Dev.to
                          <FaExternalLinkAlt className="text-sm" />
                        </a>
                      </motion.div>
                    </div>
                  )}
                </div>
              )}

              {/* Medium Articles */}
              {activePubTab === "medium" && (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <h4 className="text-2xl font-medium text-gray-100 flex items-center">
                      <SiMedium className="text-white mr-2 text-3xl" />
                      Medium Publications
                    </h4>
                    <a
                      href="https://medium.com/@codetirtho97"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-4 py-2 text-sm font-medium text-emerald-500 border border-emerald-500 hover:bg-emerald-500 hover:text-white rounded-lg shadow transition-transform transform hover:scale-105"
                    >
                      Follow Me →
                    </a>
                  </div>

                  {mediumArticles.length === 0 ? (
                    <div className="text-center py-10">
                      <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-400"></div>
                      <p className="mt-4 text-gray-400">Loading articles...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Featured Article (first article) - Larger display */}
                      {mediumArticles.length > 0 && (
                        <motion.a
                          href={mediumArticles[0].link}
                          target="_blank"
                          rel="noopener noreferrer"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4 }}
                          className="block bg-gradient-to-r from-gray-800/60 to-gray-900/60 rounded-xl p-6 border border-gray-700 hover:border-emerald-400/30 shadow-lg hover:shadow-emerald-400/5 transition-all duration-300 group"
                        >
                          <div className="flex flex-col md:flex-row gap-4 items-center">
                            {/* Article Icon - Styled Medium logo as placeholder for featured article */}
                            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center flex-shrink-0 border-2 border-white/10 group-hover:border-emerald-400/30 transition-all duration-300">
                              <SiMedium className="text-5xl text-white group-hover:text-emerald-400 transition-colors duration-300" />
                            </div>

                            <div className="flex-1 text-center md:text-left">
                              <span className="text-emerald-400 text-sm font-medium mb-2 inline-block px-3 py-1 bg-emerald-400/10 rounded-full">
                                Featured Article
                              </span>
                              <h5 className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors mb-2">
                                {mediumArticles[0].title}
                              </h5>
                              <p className="text-gray-400 text-sm mb-2">
                                {mediumArticles[0].date}
                              </p>
                              <div className="inline-flex items-center text-emerald-400 text-sm font-medium">
                                Read Article{" "}
                                <FaExternalLinkAlt className="ml-2 text-xs" />
                              </div>
                            </div>
                          </div>
                        </motion.a>
                      )}

                      {/* Article Grid */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {mediumArticles.slice(1, 7).map((article, index) => (
                          <motion.a
                            key={index}
                            href={article.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3, delay: index * 0.1 }}
                            className="bg-gray-800/40 rounded-lg p-4 border border-gray-700 hover:border-emerald-400/30 transition-all duration-300 hover:bg-gray-700/30 flex group"
                          >
                            {/* Article Date with Styled Background */}
                            <div className="flex-shrink-0 w-16 h-16 flex flex-col items-center justify-center bg-gray-900/60 rounded-lg mr-4 border border-gray-700 group-hover:border-emerald-400/20 transition-all duration-300">
                              <span className="text-gray-400 text-xs">
                                {new Date(article.date).toLocaleDateString(
                                  "en-US",
                                  { month: "short" }
                                )}
                              </span>
                              <span className="text-white text-xl font-bold">
                                {new Date(article.date).getDate()}
                              </span>
                            </div>

                            <div className="flex-1">
                              <h5 className="text-white font-medium mb-2 leading-tight line-clamp-2 group-hover:text-emerald-400 transition-colors">
                                {article.title}
                              </h5>
                              <div className="flex justify-between items-center">
                                <span className="text-gray-400 text-xs">
                                  {new Date(article.date).getFullYear()}
                                </span>
                                <span className="text-emerald-400 text-sm flex items-center opacity-0 group-hover:opacity-100 transition-opacity">
                                  Read{" "}
                                  <FaExternalLinkAlt className="ml-1 text-xs" />
                                </span>
                              </div>
                            </div>
                          </motion.a>
                        ))}
                      </div>

                      {/* See More Articles Button (if more than 7 articles) */}
                      {mediumArticles.length > 7 && (
                        <motion.div
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.6 }}
                          className="mt-4 flex justify-center"
                        >
                          <button
                            onClick={() => {
                              // Either show modal with all articles or navigate to related page
                              window.open(
                                "https://medium.com/@codetirtho97",
                                "_blank"
                              );
                            }}
                            className="px-6 py-3 bg-gradient-to-r from-emerald-500/20 to-emerald-600/20 text-emerald-400 rounded-lg hover:text-white hover:from-emerald-500 hover:to-emerald-600 transition-all duration-300 flex items-center"
                          >
                            See All Articles{" "}
                            <FaExternalLinkAlt className="ml-2" />
                          </button>
                        </motion.div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Experience;
